import { Box, Link, Stack, Typography } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SlideshowOutlinedIcon from "@mui/icons-material/SlideshowOutlined";
import { StaffMember } from "../DTOs/responses/staff";
import { uploadedFileUrl } from "../utils/uploadedFileUrl";

interface PeopleCardProps {
  member: StaffMember;
  role: string;
  activitiesLabel: string;
  featured?: boolean;
}

export default function PeopleCard({
  member,
  role,
  activitiesLabel,
  featured = false,
}: PeopleCardProps) {
  const fullName = `${member.firstName} ${member.lastName}`;
  const fallbackSrc = `/placeholder-staff-${member.gender}.jpg`;
  const photoUrl = uploadedFileUrl("pelgroupStaff", member.picture) || fallbackSrc;
  // The slides are uploaded through the admin panel as a PDF, which the link
  // below opens in a new tab.
  const slidesUrl = member.slidesLink
    ? uploadedFileUrl("pelgroupStaffSlides", member.slidesLink)
    : "";

  const photo = (
    <Box
      sx={{
        flex: featured ? { xs: "0 0 auto", sm: "0 0 200px" } : "0 0 auto",
        width: featured ? { xs: "100%", sm: 200 } : "100%",
        height: featured ? { xs: 240, sm: 240 } : 220,
        backgroundColor: "#F1EDE5",
        overflow: "hidden",
      }}
    >
      <Box
        component="img"
        src={photoUrl}
        alt={fullName}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = fallbackSrc;
        }}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center top",
          display: "block",
        }}
      />
    </Box>
  );

  const info = (
    <Stack sx={{ p: { xs: 2.2, md: 2.6 }, flex: 1, minWidth: 0 }} spacing={0.8}>
      <Typography
        component="h3"
        sx={{
          color: "#20242A",
          fontFamily: "Source Sans Pro SemiBold",
          fontSize: featured ? { xs: 22, md: 26 } : { xs: 18, md: 19 },
          lineHeight: 1.2,
        }}
      >
        {fullName}
      </Typography>

      <Typography
        sx={{
          color: "#C46A2D",
          fontFamily: "Source Sans Pro SemiBold",
          fontSize: 13,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
        }}
      >
        {role}
      </Typography>

      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
        <MailOutlineIcon sx={{ color: "#8A857C", fontSize: 17, flex: "0 0 auto" }} />
        <Link
          href={`mailto:${member.email}`}
          sx={{
            color: "#62605A",
            fontSize: { xs: 13.5, md: 14 },
            textDecoration: "none",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            "&:hover": { color: "#8A4319", textDecoration: "underline" },
          }}
        >
          {member.email}
        </Link>
      </Stack>

      {slidesUrl ? (
        <Box sx={{ mt: 0.6 }}>
          <Link
            href={slidesUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              color: "#8A4319",
              fontFamily: "Source Sans Pro SemiBold",
              fontSize: { xs: 13.5, md: 14 },
              textDecoration: "none",
              "&:hover": { color: "#C46A2D", textDecoration: "underline" },
            }}
          >
            <SlideshowOutlinedIcon sx={{ fontSize: 18 }} />
            {activitiesLabel}
          </Link>
        </Box>
      ) : null}
    </Stack>
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: featured ? { xs: "column", sm: "row" } : "column",
        border: "1px solid #E4DED2",
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: "#FFFFFF",
        boxShadow: "0 12px 30px rgba(32,36,42,0.06)",
        transition: "border-color .2s ease, box-shadow .2s ease, transform .2s ease",
        "&:hover": {
          borderColor: "#D7B38D",
          boxShadow: "0 16px 36px rgba(32,36,42,0.12)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {photo}
      {info}
    </Box>
  );
}
